import type {
  AppField,
  AppStructureFieldParams,
  FieldChoiceValue,
  LinkedRecordFieldParams,
} from "@smartsuite/types";
import { useMemo } from "react";

import { isCoverImageSupportedField } from "../../../../../helpers/coverImage";

export interface LinkedRecordCoverImageFieldProps {
  params: LinkedRecordFieldParams;
  linkedApplicationFields:
    | {
        [key: string]: AppField<AppStructureFieldParams<unknown, FieldChoiceValue>>;
      }
    | undefined;
}

/**
 * A custom hook that retrieves a linked record's cover image field based on the provided parameters.
 *
 * This function processes a field selected as the cover image from a linked application,
 * and checks if the field is supported as a cover image field. If the field is valid and supported,
 * it returns the corresponding field; otherwise, it returns undefined.
 *
 */
export const useLinkedRecordCoverImageField = ({
  params,
  linkedApplicationFields,
}: LinkedRecordCoverImageFieldProps): AppField | undefined => {
  const fieldSelectedAsCoverImage =
    params.cover_image_field && linkedApplicationFields
      ? linkedApplicationFields[params.cover_image_field]
      : undefined;

  return useMemo(() => {
    if (!fieldSelectedAsCoverImage) return undefined;
    return isCoverImageSupportedField(fieldSelectedAsCoverImage)
      ? fieldSelectedAsCoverImage
      : undefined;
  }, [fieldSelectedAsCoverImage]);
};
